// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTexts, TXT } from '../context/TextContext';
import { authApi } from '../services/api';
import CustomSelect from '../components/CustomSelect';

const COLOR = '#2a9d8f';

export default function Login() {
  const { loginWithData } = useAuth();
  const navigate   = useNavigate();
  const { t }      = useTexts();

  const REMEMBER_KEY = 'whl_remembered_login';
  const [form, setForm]         = useState({ login: localStorage.getItem(REMEMBER_KEY) || '', password: '' });
  const [remember, setRemember] = useState(!!localStorage.getItem(REMEMBER_KEY));
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [recoverMsg, setRecoverMsg] = useState('');
  const [maintenance, setMaintenance] = useState(null); // null=checking, false=ok, {message}=active
  const [companies, setCompanies] = useState(null); // null=no selector, [{empId, name}]=showing selector
  const [selectedEmpId, setSelectedEmpId] = useState('');

  useEffect(() => {
    authApi.status()
      .then(res => setMaintenance(res.data.maintenance ? { message: res.data.message } : false))
      .catch(() => setMaintenance(false)); // si falla el check, no bloquear el login
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (remember) localStorage.setItem(REMEMBER_KEY, form.login);
      else localStorage.removeItem(REMEMBER_KEY);

      const payload = { login: form.login, password: form.password };
      if (companies && selectedEmpId) payload.empId = parseInt(selectedEmpId);

      const res = await authApi.login(payload);

      if (res.data.requireCompanySelection) {
        setCompanies(res.data.companies);
        setSelectedEmpId(res.data.companies[0]?.empId?.toString() || '');
        setLoading(false);
        return;
      }

      const { token, user: userData } = res.data;
      sessionStorage.removeItem('whl_lastFilter');
      loginWithData(token, userData);
      navigate('/documents');
    } catch (err) {
      setError(err.response?.data?.error || t(TXT.WRONG_CREDENTIALS, 'Usuario o contraseña incorrectos'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({ login: '', password: '' });
    setError('');
    setCompanies(null);
    setSelectedEmpId('');
  };

  // Mientras verifica el estado del servidor, mostrar spinner mínimo
  if (maintenance === null) {
    return (
      <div style={styles.wrapper}>
        <div style={{ color: '#aaa', fontSize: '1.5rem' }}>⏳</div>
      </div>
    );
  }

  // Pantalla de mantenimiento
  if (maintenance) {
    const isEn = !(navigator.language || navigator.userLanguage || '').toLowerCase().startsWith('es');
    const mx = {
      title:   isEn ? 'System Maintenance'                                              : 'Sistema en mantenimiento',
      body:    isEn ? 'The system is temporarily unavailable for scheduled maintenance.' : 'El sistema se encuentra temporalmente fuera de servicio por mantenimiento programado.',
      sorry:   isEn ? 'We apologize for the inconvenience. Please try again later.'     : 'Disculpe las molestias. Por favor intente nuevamente más tarde.',
      loginBtn: isEn ? 'Login'                                                           : 'Ingresar',
    };
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <div style={{ ...styles.header, borderBottom: `3px solid ${COLOR}` }}>
            <h5 style={{ ...styles.title, color: COLOR, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={styles.wrenchIcon}>🔧</span>
              {mx.title}
            </h5>
          </div>
          <div style={{ padding: '2rem 1.5rem', textAlign: 'center' }}>
            <div style={styles.gearWrapper}>
              <span style={styles.gearBig}>⚙️</span>
              <span style={styles.gearSmall}>⚙️</span>
            </div>
            <p style={{ color: '#555', fontSize: '0.95rem', marginTop: '1.25rem', marginBottom: maintenance.message ? '1rem' : 0, lineHeight: 1.6 }}>
              {mx.body}
            </p>
            {maintenance.message && (
              <div style={styles.msgBox}>
                {isEn
                  ? `Scheduled maintenance until ${maintenance.message}`
                  : `Mantenimiento programado hasta las ${maintenance.message}`}
              </div>
            )}
            <p style={{ color: '#aaa', fontSize: '0.78rem', marginTop: '1.5rem' }}>
              {mx.sorry}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>

        {/* Panel de marca */}
        <div style={styles.brandPanel}>
          {/* Círculo decorativo */}
          <div style={styles.brandCircle} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={styles.brandName}>Recibido</div>
            <div style={styles.brandTagline}>{t(TXT.LOGIN_BRAND_TAGLINE, 'Gestión documental')}</div>
          </div>
        </div>

        {/* Panel de formulario */}
        <div style={styles.formPanel}>
          <div style={styles.formHeader}>
            <h5 style={styles.title}>{t(TXT.LOGIN_TITLE, 'System access')}</h5>
          </div>

          <form onSubmit={handleSubmit} style={styles.formBody}>
            {/* Campo usuario */}
            <div style={styles.fieldRow}>
              <label style={styles.label}>{t(TXT.USER_LABEL, 'User:')}</label>
              <input
                style={styles.input}
                type="text"
                name="login"
                value={form.login}
                onChange={handleChange}
                placeholder={t(TXT.ENTER_USER, 'Enter user')}
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            {/* Campo contraseña */}
            <div style={styles.fieldRow}>
              <label style={styles.label}>{t(TXT.PASSWORD_LABEL, 'Password:')}</label>
              <input
                style={styles.input}
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder={t(TXT.PASSWORD, 'Password')}
                required
                autoComplete="current-password"
              />
            </div>

            {/* Recordar usuario */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="remember"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                style={styles.checkbox}
              />
              <label htmlFor="remember" style={{ fontSize: '0.82rem', color: '#5a8a86', cursor: 'pointer', margin: 0 }}>
                {t(TXT.REMEMBER_ME, 'Remember me on this device')}
              </label>
            </div>

            {/* Selector de empresa (multi-empresa) */}
            {companies && (
              <div style={styles.fieldRow}>
                <label style={styles.label}>Empresa:</label>
                <CustomSelect
                  value={selectedEmpId}
                  onChange={e => setSelectedEmpId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {companies.map(c => (
                    <option key={c.empId} value={c.empId}>{c.name}</option>
                  ))}
                </CustomSelect>
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{ color: '#d32f2f', fontSize: '0.82rem' }}>{error}</div>
            )}

            {/* Botones */}
            <div style={styles.buttonRow}>
              <button type="button" className="button" onClick={handleCancel} disabled={loading}>
                {t(TXT.CANCEL, 'Cancel')}
              </button>
              <button type="submit" className="button color1" disabled={loading}>
                {loading ? '…' : t(TXT.CONFIRM, 'Confirm')}
              </button>
            </div>

            {/* Recuperar contraseña */}
            <div style={{ textAlign: 'right' }}>
              <button
                type="button"
                className="button"
                style={{ fontSize: '0.8rem' }}
                onClick={async () => {
                  const loginVal = form.login.trim();
                  if (!loginVal) { setError(t(TXT.ENTER_USER, 'Ingrese su usuario')); return; }
                  try {
                    await authApi.recoverPassword(loginVal);
                    setRecoverMsg(t(TXT.MAIL_SENT, 'Mail enviado con la contraseña'));
                  } catch { setError(t(TXT.TRANSACTION_ERROR, 'Error')); }
                }}
              >
                {t(TXT.RECOVER_PASSWORD, 'Recover password')}
              </button>
              {recoverMsg && <p style={{ color: COLOR, fontSize: '0.82rem', marginTop: '0.3rem' }}>{recoverMsg}</p>}
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3d7b73',
  },
  card: {
    display: 'flex',
    width: '100%',
    maxWidth: '620px',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    margin: '16px',
  },
  // Panel izquierdo (marca)
  brandPanel: {
    flex: '0 0 200px',
    background: 'linear-gradient(160deg, #4a9089 0%, #3d7b73 60%, #2f6059 100%)',
    padding: '36px 28px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
  },
  brandName: {
    fontFamily: 'Georgia, serif',
    fontSize: '1.6rem',
    color: '#ffffff',
    fontWeight: '400',
    letterSpacing: '-0.3px',
  },
  brandTagline: {
    fontSize: '0.7rem',
    color: 'rgba(255,255,255,0.55)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginTop: '5px',
    fontWeight: '400',
  },
  brandMsg: {
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.45)',
    lineHeight: '1.55',
    fontWeight: '300',
  },
  brandCircle: {
    position: 'absolute',
    bottom: '-70px',
    right: '-70px',
    width: '220px',
    height: '220px',
    borderRadius: '50%',
    border: '36px solid rgba(255,255,255,0.07)',
    zIndex: 0,
  },
  // Panel derecho (formulario)
  formPanel: {
    flex: '1',
    backgroundColor: '#f7f5f2',
    padding: '32px 32px 28px',
    display: 'flex',
    flexDirection: 'column',
  },
  formHeader: {
    marginBottom: '20px',
    paddingBottom: '14px',
    borderBottom: '1px solid #e8f5f3',
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    flex: 1,
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2a4542',
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  label: {
    fontSize: '0.72rem',
    fontWeight: '500',
    color: '#4e706c',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  input: {
    padding: '9px 12px',
    border: '1.5px solid #cce5e2',
    borderRadius: '7px',
    fontSize: '0.88rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    color: '#2a4542',
  },
  checkbox: {
    appearance: 'none',
    WebkitAppearance: 'none',
    width: '15px',
    height: '15px',
    border: '1.5px solid #cce5e2',
    borderRadius: '3px',
    cursor: 'pointer',
    position: 'relative',
    flexShrink: 0,
    accentColor: COLOR,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '4px',
  },
  divider: { borderTop: '1px solid #e8f5f3' },
  btn: {
    padding: '0.4rem 1.1rem',
    backgroundColor: COLOR,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  gearWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'flex-end',
    gap: '0.1rem',
    marginTop: '0.5rem',
  },
  gearBig: {
    fontSize: '3.5rem',
    display: 'inline-block',
    animation: 'whl-spin 4s linear infinite',
  },
  gearSmall: {
    fontSize: '2rem',
    display: 'inline-block',
    animation: 'whl-spin-rev 4s linear infinite',
    marginBottom: '0.4rem',
  },
  wrenchIcon: {
    fontSize: '1rem',
  },
  msgBox: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '6px',
    padding: '0.6rem 1rem',
    color: '#795548',
    fontSize: '0.87rem',
    textAlign: 'left',
    lineHeight: 1.5,
  },
};
